import { addressSchema } from '@omecare/utils';
import {
  UseMutationResult,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { z } from 'zod';
import { AddOfficeFormValues } from '@/components/forms/add-office/schema';
import http from '@/utils/http.service';

const addOfficeResponseSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  address: addressSchema,
  nationalMedicalOrderMail: z.string(),
  estimatedCaPerSlot: z.number(),
  averageSlotHonorary: z.coerce.number().default(0),
  withAssistantRetrocessionRate: z.coerce.number(),
  withoutAssistantRetrocessionRate: z.coerce.number(),
  weekendRetrocessionRate: z.coerce.number(),
});

export type AddOfficeResponse = z.infer<typeof addOfficeResponseSchema>;
export type AddOfficePayload = AddOfficeFormValues;

const addOffice = (payload: AddOfficePayload) => {
  return http.offices
    .create({
      ...payload,
      site: payload.siteId,
      address: {
        ...payload.address,
        state: 'France',
      },
    })
    .then<AddOfficeResponse>(({ response, transform }) => {
      return transform.handle({
        response,
        attributes: {
          name: true,
          address: true,
          nationalMedicalOrderMail: true,
          estimatedCaPerSlot: true,
          averageSlotHonorary: true,
          withAssistantRetrocessionRate: true,
          withoutAssistantRetrocessionRate: true,
          weekendRetrocessionRate: true,
        },
        schema: addOfficeResponseSchema,
      });
    });
};

const useAddOfficeMutation = (): UseMutationResult<
  AddOfficeResponse,
  Error,
  AddOfficePayload
> => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: addOffice,
    onSuccess() {
      toast.success(t('http.add-office.success'));

      queryClient.invalidateQueries({
        queryKey: ['offices-agendas'],
      });
    },
  });
};

export default useAddOfficeMutation;
